import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGlobalVariable,
  useUpdateGlobalVariable
} from '@api/enterprise/globalVariable';
import { globalVariablesKeys } from '@api/enterprise/globalVariable/constants';
import type {
  EquipmentAssessmentConfig,
  AssessmentConfigFormData,
  ValidationErrors
} from '../types';
import {
  parseAssessmentConfig,
  stringifyAssessmentConfig,
  validateAssessmentConfig,
  formDataToConfig,
  configToFormData
} from '../utils';

const VARIABLE_NAME = 'HIDE_ASSESSMENT_CONDITIONS';

export const useAssessmentConfigData = () => {
  const queryClient = useQueryClient();

  const {
    data: globalVariable,
    isLoading,
    error,
    refetch
  } = useGlobalVariable(VARIABLE_NAME);

  const updateMutation = useUpdateGlobalVariable();

  const [localFormData, setLocalFormData] = useState<AssessmentConfigFormData>({
    conditionValues: '',
    conditionRequired: false,
    operabilityValues: '',
    operabilityRequired: false
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  const originalConfig = useMemo((): EquipmentAssessmentConfig => {
    return parseAssessmentConfig(globalVariable?.blobText);
  }, [globalVariable?.blobText]);

  useEffect(() => {
    const formData = configToFormData(originalConfig);
    setLocalFormData(formData);
    setHasUnsavedChanges(false);
    setValidationErrors({});
  }, [originalConfig]);

  const updateField = useCallback(
    <K extends keyof AssessmentConfigFormData>(
      field: K,
      value: AssessmentConfigFormData[K]
    ) => {
      setLocalFormData(prev => ({ ...prev, [field]: value }));
      setHasUnsavedChanges(true);
      if (field === 'conditionValues') {
        setValidationErrors(prev => ({ ...prev, conditionValues: undefined }));
      } else if (field === 'operabilityValues') {
        setValidationErrors(prev => ({
          ...prev,
          operabilityValues: undefined
        }));
      }
    },
    []
  );

  const saveChanges = useCallback(async () => {
    const validation = validateAssessmentConfig(localFormData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return { success: false, errors: validation.errors };
    }

    try {
      const config = formDataToConfig(localFormData);
      const jsonString = stringifyAssessmentConfig(config);

      await updateMutation.mutateAsync({
        name: VARIABLE_NAME,
        command: {
          blobText: jsonString,
          text: globalVariable?.text || null,
          int: globalVariable?.int || null,
          date: globalVariable?.date || null,
          numeric: globalVariable?.numeric || null
        }
      });

      await queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(VARIABLE_NAME)
      });

      await refetch();
      setHasUnsavedChanges(false);
      setValidationErrors({});

      return { success: true };
    } catch (err) {
      setHasUnsavedChanges(true);
      throw err;
    }
  }, [localFormData, globalVariable, updateMutation, queryClient, refetch]);

  const discardChanges = useCallback(() => {
    const formData = configToFormData(originalConfig);
    setLocalFormData(formData);
    setHasUnsavedChanges(false);
    setValidationErrors({});
  }, [originalConfig]);

  return {
    formData: localFormData,
    originalConfig,
    isLoading,
    error,
    hasUnsavedChanges,
    isSaving: updateMutation.isPending,
    saveError: updateMutation.error,
    validationErrors,
    updateField,
    saveChanges,
    discardChanges,
    refresh: refetch
  };
};

export default useAssessmentConfigData;
